/**
 * Emoji Picker Component
 * 
 * Full-featured emoji picker using @emoji-mart/react with:
 * - Recently used emojis (localStorage)
 * - i18n support
 * - Keyboard shortcuts
 * - Optimized rendering
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Popover } from 'antd';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { useTranslation } from 'react-i18next';

/**
 * Emoji data structure from emoji-mart
 */
interface EmojiMartData {
  native?: string;
  unified?: string;
  shortcodes?: string;
  id?: string;
  name?: string;
  keywords?: string[];
  [key: string]: unknown;
}

/**
 * Props for emoji-mart Picker component
 */
interface EmojiMartPickerProps {
  data: typeof data;
  onEmojiSelect: (emoji: EmojiMartData) => void;
  locale?: string;
  theme?: 'light' | 'dark' | 'auto';
  previewPosition?: 'none' | 'top' | 'bottom';
  skinTonePosition?: 'search' | 'preview' | 'none';
  perLine?: number;
  emojiSize?: number;
  emojiButtonSize?: number;
  maxFrequentRows?: number;
  categories?: string[];
}

interface EmojiPickerProps {
  onEmojiSelect: (emoji: string) => void;
  children: React.ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Storage key for recently used emojis
 */
const RECENT_EMOJIS_KEY = 'chat_recent_emojis';

/**
 * Maximum number of recently used emojis to store
 */
const MAX_RECENT_EMOJIS = 20;

/**
 * Get recently used emojis from localStorage
 */
const getRecentEmojis = (): string[] => {
  try {
    const stored = localStorage.getItem(RECENT_EMOJIS_KEY);
    if (stored) {
      return JSON.parse(stored) as string[];
    }
  } catch (error) {
    console.error('Failed to load recent emojis:', error);
  }
  return [];
};

/**
 * Save emoji to recently used list
 */
const saveRecentEmoji = (emoji: string): void => {
  try {
    const recent = getRecentEmojis();
    // Remove if already exists
    const filtered = recent.filter((e) => e !== emoji);
    // Add to beginning
    const updated = [emoji, ...filtered].slice(0, MAX_RECENT_EMOJIS);
    localStorage.setItem(RECENT_EMOJIS_KEY, JSON.stringify(updated));
  } catch (error) {
    console.error('Failed to save recent emoji:', error);
  }
};

/**
 * Emoji Picker Component
 * 
 * Wraps @emoji-mart/react Picker with Popover and additional features
 */
export const EmojiPicker: React.FC<EmojiPickerProps> = ({
  onEmojiSelect,
  children,
  placement = 'top',
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}) => {
  const { i18n } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);
  const [recentEmojis, setRecentEmojis] = useState<string[]>([]);

  // Use controlled or uncontrolled state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange || setInternalOpen;

  // Load recent emojis on mount
  useEffect(() => {
    setRecentEmojis(getRecentEmojis());
  }, []);

  // Get current language for emoji picker
  const emojiLocale = useMemo(() => {
    const lang = i18n.language;
    // Map i18n language codes to emoji-mart locale codes
    const localeMap: Record<string, string> = {
      en: 'en',
      el: 'el', // Greek
      // Add more mappings as needed
    };
    return localeMap[lang] || 'en';
  }, [i18n.language]);

  // Handle emoji selection
  const handleEmojiSelect = useCallback(
    (emoji: EmojiMartData) => {
      if (emoji.native) {
        // Save to recently used
        const emojiNative = emoji.native;
        if (emojiNative) {
          saveRecentEmoji(emojiNative);
          setRecentEmojis((prev) => {
            const filtered = prev.filter((e) => e !== emojiNative);
            return [emojiNative, ...filtered].slice(0, MAX_RECENT_EMOJIS);
          });
        }

        // Call parent handler
        onEmojiSelect(emoji.native);
        
        // Close picker after selection
        setOpen(false);
      }
    },
    [onEmojiSelect, setOpen]
  );

  // Picker configuration
  const pickerConfig: EmojiMartPickerProps = useMemo(
    () => ({
      data,
      onEmojiSelect: handleEmojiSelect,
      locale: emojiLocale,
      theme: 'light',
      previewPosition: 'none', // Hide preview for compact UI
      skinTonePosition: 'search', // Move skin tone to search
      perLine: 8, // Number of emojis per line
      emojiSize: 24, // Size of emoji icons
      emojiButtonSize: 36, // Size of emoji buttons
      maxFrequentRows: recentEmojis.length > 0 ? 1 : 0, // Show frequent emojis if available
      // Custom categories (optional - can be customized)
      categories: ['frequent', 'people', 'nature', 'foods', 'activity', 'places', 'objects', 'symbols', 'flags'],
    }),
    [handleEmojiSelect, emojiLocale, recentEmojis.length]
  );

  // Memoize picker content to prevent unnecessary re-renders
  const pickerContent = useMemo(
    () => (
      <div style={{ width: '352px', maxHeight: '435px' }}>
        <Picker {...pickerConfig} />
      </div>
    ),
    [pickerConfig]
  );

  return (
    <Popover
      content={pickerContent}
      trigger="click"
      placement={placement}
      open={open}
      onOpenChange={setOpen}
      styles={{
        body: { padding: '8px' },
      }}
      destroyOnHidden
    >
      {children}
    </Popover>
  );
};

